/**
 * Fase 2A · P0-1 · EmpleadoHome
 * Home del rol EMPLEADO (operario del centro). Sin acceso financiero.
 * Blueprint §7.1: Saludo · Próximas 3 citas hoy · Chats pendientes · CTAs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Skeleton } from "../../components/ui/skeleton";
import {
  CalendarBlank, ChatCircleDots, User as UserIcon, ArrowRight,
  MapPin, Clock, CarSimple, Users as UsersIcon,
} from "@phosphor-icons/react";

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 20) return "Buenas tardes";
  return "Buenas noches";
}

function isToday(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
  } catch { return false; }
}

function isThisWeek(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff >= -1 && diff <= 7;
  } catch { return false; }
}

export default function EmpleadoHome() {
  const { user, org } = useAuth();
  const [appts, setAppts] = useState(null);
  const [customersCount, setCustomersCount] = useState(0);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.allSettled([
        api.get("/appointments"),
        api.get("/customers?limit=200"),
      ]);
      const items = a.status === "fulfilled" ? (a.value.data || []) : [];
      setAppts(items);
      const customersData = c.status === "fulfilled" ? c.value.data : [];
      setCustomersCount(Array.isArray(customersData) ? customersData.length : (customersData?.total || 0));
    })();
  }, []);

  if (appts === null) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-sm" />
        <Skeleton className="h-40 rounded-sm" />
      </div>
    );
  }

  const todayAppts = appts.filter((a) => isToday(a.date) && a.status !== "Cancelado")
                          .sort((x, y) => new Date(x.date) - new Date(y.date));
  const upcomingWeek = appts.filter((a) => !isToday(a.date) && isThisWeek(a.date) && a.status !== "Cancelado")
                            .sort((x, y) => new Date(x.date) - new Date(y.date));

  const firstName = (user?.name || "").split(" ")[0] || "Operario";

  return (
    <div className="space-y-6" data-testid="empleado-home">
      {/* Hero saludo */}
      <header data-testid="empleado-greeting">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
          {greetingByHour()} · {org?.name || "Tu centro"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
          Hola, {firstName}
        </h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          {todayAppts.length > 0
            ? `Tienes ${todayAppts.length} ${todayAppts.length === 1 ? "cita" : "citas"} hoy.`
            : "Hoy no tienes citas asignadas."}
        </p>
      </header>

      {/* KPIs simples · sin datos financieros */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="ag-card p-4" data-testid="empleado-kpi-today">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] flex items-center gap-1.5">
            <CalendarBlank size={11} weight="bold" /> Hoy
          </div>
          <div className="font-display font-black text-3xl mt-1 tabular-nums text-[var(--ag-gold)]">
            {todayAppts.length}
          </div>
        </div>
        <div className="ag-card p-4" data-testid="empleado-kpi-week">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] flex items-center gap-1.5">
            <Clock size={11} weight="bold" /> Próximos 7 días
          </div>
          <div className="font-display font-black text-3xl mt-1 tabular-nums">
            {upcomingWeek.length}
          </div>
        </div>
        <div className="ag-card p-4 col-span-2 md:col-span-1" data-testid="empleado-kpi-customers">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] flex items-center gap-1.5">
            <UsersIcon size={11} weight="bold" /> Clientes centro
          </div>
          <div className="font-display font-black text-3xl mt-1 tabular-nums">
            {customersCount}
          </div>
        </div>
      </div>

      {/* Próximas 3 citas hoy · protagonista */}
      <section className="ag-card p-5" data-testid="empleado-today-section">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] flex items-center gap-2">
            <CalendarBlank weight="duotone" /> Tus citas de hoy
          </div>
          <Link to="/empleado/agenda"
            className="text-xs text-[var(--ag-gold)] flex items-center gap-1 hover:underline"
            data-testid="empleado-view-agenda-link"
          >
            Ver agenda completa <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
        {todayAppts.length === 0 ? (
          <div className="py-8 text-center" data-testid="empleado-no-today">
            <div className="text-sm text-[var(--ag-text-dim)] mb-3">
              Hoy no tienes citas asignadas.
            </div>
            <Link to="/empleado/agenda"
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] rounded text-xs font-medium hover:bg-[var(--ag-gold)]/10"
            >
              Ver agenda de la semana <ArrowRight size={12} />
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {todayAppts.slice(0, 3).map((a) => (
              <AppointmentRow key={a.id} appt={a} />
            ))}
            {todayAppts.length > 3 && (
              <Link to="/empleado/agenda"
                className="block text-center text-xs text-[var(--ag-gold)] hover:underline pt-2"
                data-testid="empleado-more-today-link"
              >
                +{todayAppts.length - 3} más hoy · Ver agenda
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Accesos rápidos */}
      <section className="grid grid-cols-2 gap-3" data-testid="empleado-quick-actions">
        <Link to="/empleado/clientes"
          className="ag-card p-4 hover:border-[var(--ag-gold)]/40 transition-colors"
          data-testid="empleado-quick-clientes"
        >
          <UsersIcon size={20} weight="duotone" className="text-[var(--ag-gold)]" />
          <div className="font-display font-bold text-base mt-2">Clientes</div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Ver clientes del centro</div>
        </Link>
        <Link to="/chats"
          className="ag-card p-4 hover:border-[var(--ag-gold)]/40 transition-colors"
          data-testid="empleado-quick-chats"
        >
          <ChatCircleDots size={20} weight="duotone" className="text-[var(--ag-gold)]" />
          <div className="font-display font-bold text-base mt-2">Chats</div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Mensajes con equipo y clientes</div>
        </Link>
        <Link to="/empleado/agenda"
          className="ag-card p-4 hover:border-[var(--ag-gold)]/40 transition-colors"
          data-testid="empleado-quick-agenda"
        >
          <CalendarBlank size={20} weight="duotone" className="text-[var(--ag-gold)]" />
          <div className="font-display font-bold text-base mt-2">Agenda</div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Citas del centro</div>
        </Link>
        <Link to="/empleado/mi-perfil"
          className="ag-card p-4 hover:border-[var(--ag-gold)]/40 transition-colors"
          data-testid="empleado-quick-perfil"
        >
          <UserIcon size={20} weight="duotone" className="text-[var(--ag-gold)]" />
          <div className="font-display font-bold text-base mt-2">Mi perfil</div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Datos personales</div>
        </Link>
      </section>

      {/* Próxima semana · resumen */}
      {upcomingWeek.length > 0 && (
        <section className="ag-card p-5" data-testid="empleado-week-section">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] flex items-center gap-2 mb-3">
            <Clock weight="duotone" /> Próximas de la semana
          </div>
          <div className="space-y-2">
            {upcomingWeek.slice(0, 4).map((a) => (
              <AppointmentRow key={a.id} appt={a} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function AppointmentRow({ appt, compact = false }) {
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5 border-b border-[var(--ag-border)] last:border-0"
      data-testid={`empleado-appt-${appt.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <CarSimple size={14} weight="duotone" className="text-[var(--ag-gold)] shrink-0" />
          <span className="font-mono-tab font-semibold text-sm">{appt.license_plate || "—"}</span>
          <span className="text-xs text-[var(--ag-text-dim)]">·</span>
          <span className="text-xs text-[var(--ag-text)]">{appt.wash_type}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--ag-text-dim)] mt-1">
          <Clock size={11} />
          <span className="tabular-nums">{fmtDateTime(appt.date)}</span>
          {appt.customer_name && !compact && (
            <>
              <span>·</span>
              <span className="truncate">{appt.customer_name}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
          appt.status === "Pendiente" ? "bg-amber-500/15 text-amber-300"
          : appt.status === "Completada" ? "bg-emerald-500/15 text-emerald-300"
          : "bg-[var(--ag-midnight)] text-[var(--ag-text-dim)]"
        }`}>
          {appt.status || "—"}
        </span>
      </div>
    </div>
  );
}
